import React, { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import AdminLayout from "@/components/settings/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { fetchTemplateAgents, createTemplateAgent, updateTemplateAgentBanners } from "@/services/templateService";
import { useAuth } from "@/contexts/AuthContext";
import { Json } from "@/integrations/supabase/types";

type TemplateAgent = {
  id: string;
  title: string;
  description: string | null;
  agent_name: string | null;
  banner: string | null;
  category: string | null;
  [key: string]: any;
};

const templateSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  agent_name: z.string().optional(),
  welcome_message: z.string().optional(),
  category: z.string().optional(),
  banner: z.string().optional().nullable(),
});

const AdminTemplatesPage = () => {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user, organization } = useAuth();
  
  const form = useForm<z.infer<typeof templateSchema>>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      title: "",
      description: "",
      agent_name: "",
      welcome_message: "",
      category: "law",
      banner: null,
    },
  });

  const { 
    data: templateAgents, 
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['templateAgents'],
    queryFn: async () => {
      await updateTemplateAgentBanners();
      
      const result = await fetchTemplateAgents();
      if (result.error) throw result.error;
      
      if (!result.data) return [];
      
      const agentsWithBanner = result.data.map(agent => ({
        ...agent,
        banner: agent.banner || null
      }));
      
      return agentsWithBanner as unknown as TemplateAgent[];
    },
  });

  useEffect(() => {
  }, [organization, user, refetch, toast]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch().finally(() => setIsRefreshing(false));
  };

  const handleSelectTemplate = (template: any) => {
    toast({
      title: "Template Selected",
      description: `You selected the ${template.title} template. Implementation coming soon!`,
    });
  };

  const handleCreateTemplate = async (values: z.infer<typeof templateSchema>) => {
    try {
      const result = await createTemplateAgent(values);
      
      if (result.error) {
        throw result.error;
      }
      
      toast({
        title: "Template Created",
        description: "Template agent has been created successfully.",
      });
      
      setIsCreateDialogOpen(false);
      form.reset();
      refetch();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Failed to create template",
        description: error.message || "An unexpected error occurred.",
      });
    }
  };

  if (error) {
    toast({
      variant: "destructive",
      title: "Failed to load templates",
      description: (error as Error).message || "An unexpected error occurred.",
    });
  }

  return (
    <MainLayout>
      <AdminLayout 
        title="Agent Templates" 
        description="Manage and organize agent templates for organizations"
      >
        <Tabs defaultValue="templates">
          <div className="flex justify-between items-center mb-6">
            <TabsList>
              <TabsTrigger value="templates">Templates</TabsTrigger>
              <TabsTrigger value="categories">Categories</TabsTrigger>
            </TabsList>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button size="sm" onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Template
              </Button>
            </div>
          </div>
          
          <TabsContent value="templates">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Agent Templates</CardTitle>
                <CardDescription>
                  Browse and manage templates that organizations can use to quickly create agents
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                  </div>
                ) : templateAgents && templateAgents.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templateAgents.map((template) => (
                      <Card 
                        key={template.id} 
                        className="overflow-hidden hover:shadow-md transition-shadow border-muted/50 bg-[#2a2a2a] text-white h-full flex flex-col"
                      >
                        {template.banner && (
                          <div className="w-full h-48 overflow-hidden">
                            <img 
                              src={template.banner} 
                              alt={template.title} 
                              className="w-full h-full object-cover" 
                            />
                          </div>
                        )}
                        <CardHeader className="pb-2">
                          <div className="flex items-start">
                            <div className="h-12 w-12 flex items-center justify-center text-4xl bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl mr-4">
                              {template.agent_name?.[0] || template.title[0]}
                            </div>
                            <div>
                              <CardTitle className="text-lg md:text-xl text-white">
                                {template.title}
                              </CardTitle>
                              <p className="text-sm text-muted-foreground mt-1">
                                {template.description?.substring(0, 60)}
                                {template.description && template.description.length > 60 ? "..." : ""}
                              </p>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="flex-grow">
                          <p className="text-sm text-white/80">
                            {template.description?.substring(0, 120)}
                            {template.description && template.description.length > 120 ? "..." : ""}
                          </p>
                          <div className="mt-2">
                            <span className="text-xs bg-primary/20 text-primary-foreground px-2 py-1 rounded-full">
                              {template.category || "General"}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">
                    No templates found. Create your first template to get started.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <CardTitle>Template Categories</CardTitle>
                <CardDescription>
                  Manage categories for organizing agent templates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-center py-8 text-muted-foreground">
                  Category management coming soon
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </AdminLayout>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Template</DialogTitle>
            <DialogDescription>
              Create a new agent template that organizations can use to quickly set up agents.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleCreateTemplate)} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Customer Support Bot" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Template description and use cases" {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="agent_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agent Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Customer Support" {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="welcome_message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Welcome Message</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Hello! How can I help you today?" {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <Input placeholder="Law" {...field} value={field.value || 'law'} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Template</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default AdminTemplatesPage;
